import { z } from 'zod/v4';
import { AmforiSyncLastErrorSchema } from './sync-last-error.js';
import {
  AmforiSyncStatusSchema,
  AmforiSyncTriggerSourceSchema,
} from './sync-status.js';
import { AmforiSyncReportArchiveSchema } from './sync-warning-archive.js';

/**
 * Display status for a single report entry. Maps the step-log enum
 * (`OK | WARN | ERROR`) to the UI vocabulary (`SUCCESS | WARNING | ERROR`);
 * `SKIP` is reserved for forward-compatibility.
 */
export const AmforiSyncReportEntryStatusSchema = z.enum([
  'SUCCESS',
  'WARNING',
  'ERROR',
  'SKIP',
]);

/** A single pre-extracted context field (`key` so the FE owns the i18n label). */
export const AmforiSyncReportContextFieldSchema = z.object({
  key: z.string(),
  value: z.string(),
});

/** One step-log entry, enriched for the in-app report page. */
export const AmforiSyncReportEntrySchema = z.object({
  id: z.string(),
  code: z.string(),
  status: AmforiSyncReportEntryStatusSchema,
  /** UI phase label, e.g. "Master data — supplier matching". */
  phase: z.string(),
  message: z.string(),
  suggestedFix: z.string().nullable(),
  /** Raw step-log context (jsonb) — the FE may read specific fields directly. */
  context: z.record(z.string(), z.unknown()).nullable(),
  /** Pre-extracted, ordered display fields (per-code curation + value formatting). */
  fields: z.array(AmforiSyncReportContextFieldSchema),
  createdAt: z.string(),
  /**
   * Server-derived (DELTA-2254): `true` on a `SUPPLIER_MATCHED` entry whose
   * matched supplier has since been UNLINKED (its amfori Site ID cleared). The
   * historical step-log row is never mutated; this flag is computed at read time
   * from the supplier's current link state so the UI can render an "Unlinked"
   * badge. Absent/`false` for every other entry.
   */
  unlinked: z.boolean().optional(),
  /**
   * Server-derived (DELTA-2764): `true` on a `SUPPLIER_MATCHED` entry whose
   * supplier's amfori Site ID another company set and still owns, so the
   * viewing brand is offered no Unlink action. Computed at read time, like
   * `unlinked`; never names the owner.
   */
  locked: z.boolean().optional(),
  /**
   * Server-derived (DELTA-2763): `true` on a supplier-matching warning the
   * viewing brand may dismiss into its archive, or restore from it. Only the
   * run's owner gets it.
   */
  dismissible: z.boolean().optional(),
  /**
   * Server-derived (DELTA-2763): the warning's place in the run owner's
   * archive — dismissed (`ARCHIVED`, by whom and when), or come back on its
   * own (`BACK`, when and why). Absent when it is not archived.
   */
  archive: AmforiSyncReportArchiveSchema.optional(),
});

/**
 * The full payload behind `GET /integrations/amfori/history/:syncHistoryId` —
 * header + the seven counters + every step-log entry (PDF-consistent order).
 */
export const AmforiSyncReportPayloadSchema = z.object({
  header: z.object({
    syncHistoryId: z.string(),
    trigger: AmforiSyncTriggerSourceSchema,
    status: AmforiSyncStatusSchema,
    /** `YYYY-MM-DD` derived from `startedAt`. */
    syncDate: z.string(),
    startedAt: z.string(),
    completedAt: z.string().nullable(),
    durationMs: z.number().nullable(),
    /** Structured failure classification on FAILED runs; null otherwise. */
    lastError: AmforiSyncLastErrorSchema.nullable(),
  }),
  counters: z.object({
    suppliersMatched: z.number(),
    suppliersUnmatched: z.number(),
    auditsCreated: z.number(),
    dataPointsWritten: z.number(),
    findingsCreated: z.number(),
    zeroToleranceCreated: z.number(),
    errorsCount: z.number(),
  }),
  entries: z.array(AmforiSyncReportEntrySchema),
});

export type AmforiSyncReportEntryStatus = z.infer<
  typeof AmforiSyncReportEntryStatusSchema
>;
export type AmforiSyncReportContextField = z.infer<
  typeof AmforiSyncReportContextFieldSchema
>;
export type AmforiSyncReportEntry = z.infer<typeof AmforiSyncReportEntrySchema>;
export type AmforiSyncReportPayload = z.infer<
  typeof AmforiSyncReportPayloadSchema
>;
